import { Row, Col, Card, Empty, Spin } from 'antd';
import { Pie, Column, Line } from '@ant-design/charts';
import dayjs from 'dayjs';
import type { MetricsResponse, ChartsResponse } from '../../api/types';

interface DashboardChartsProps {
  metrics: MetricsResponse | undefined;
  charts: ChartsResponse | undefined;
  loading: boolean;
}

export function DashboardCharts({ metrics, charts, loading }: DashboardChartsProps) {
  if (loading) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3, 4].map((i) => (
          <Col xs={24} lg={12} key={i}>
            <Card style={{ height: 300 }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Spin />
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  // Pie chart data for job status distribution
  const pieData = metrics
    ? [
        { type: 'Completed', value: metrics.total_jobs - metrics.failed_jobs - metrics.active_jobs },
        { type: 'Failed', value: metrics.failed_jobs },
        { type: 'Active', value: metrics.active_jobs },
      ].filter((d) => d.value > 0)
    : [];

  // Format hour for display
  const formatHour = (hour: string) => {
    return dayjs(hour).format('HH:mm');
  };

  // Jobs by hour data for stacked bar chart
  const jobsByHourData = charts?.jobs_by_hour.flatMap((item) => [
    { hour: formatHour(item.hour), type: 'Completed', value: item.completed },
    { hour: formatHour(item.hour), type: 'Failed', value: item.failed },
  ]) || [];

  // Durations by hour by namespace data for multi-line chart
  const durationsData = charts?.durations_by_hour.map((item) => ({
    hour: formatHour(item.hour),
    namespace: item.namespace,
    duration: item.avg_duration_ms / 1000, // Convert to seconds
    jobs: item.job_count,
  })) || [];

  // Events per hour data for bar chart
  const eventsData = charts?.events_per_hour.map((item) => ({
    hour: formatHour(item.hour),
    events: item.event_count,
  })) || [];

  const pieConfig = {
    data: pieData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: {
      text: 'value',
      style: {
        fontWeight: 'bold',
      },
    },
    legend: {
      color: {
        title: false,
        position: 'bottom' as const,
      },
    },
    color: ['#52c41a', '#ff4d4f', '#1890ff'],
  };

  const jobsBarConfig = {
    data: jobsByHourData,
    xField: 'hour',
    yField: 'value',
    colorField: 'type',
    stack: true,
    color: ['#52c41a', '#ff4d4f'],
    legend: {
      color: {
        position: 'top' as const,
      },
    },
    axis: {
      x: {
        title: 'Hour',
      },
      y: {
        title: 'Jobs',
      },
    },
  };

  const durationLineConfig = {
    data: durationsData,
    xField: 'hour',
    yField: 'duration',
    colorField: 'namespace',
    smooth: true,
    point: {
      size: 3,
      shape: 'circle',
    },
    legend: {
      color: {
        position: 'top' as const,
      },
    },
    axis: {
      x: {
        title: 'Hour',
      },
      y: {
        title: 'Duration (seconds)',
      },
    },
    tooltip: {
      items: [
        { field: 'namespace', name: 'Namespace' },
        { field: 'duration', name: 'Avg Duration (s)' },
        { field: 'jobs', name: 'Jobs' },
      ],
    },
  };

  const eventsBarConfig = {
    data: eventsData,
    xField: 'hour',
    yField: 'events',
    color: '#722ed1',
    axis: {
      x: {
        title: 'Hour',
      },
      y: {
        title: 'Events',
      },
    },
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card title="Job Status Distribution" style={{ height: 350 }}>
          {pieData.length > 0 ? (
            <Pie {...pieConfig} height={280} />
          ) : (
            <Empty description="No job data" style={{ marginTop: 80 }} />
          )}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Jobs by Hour (Completed vs Failed)" style={{ height: 350 }}>
          {jobsByHourData.length > 0 ? (
            <Column {...jobsBarConfig} height={280} />
          ) : (
            <Empty description="No data for time range" style={{ marginTop: 80 }} />
          )}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Avg Job Duration by Hour (by Namespace)" style={{ height: 350 }}>
          {durationsData.length > 0 ? (
            <Line {...durationLineConfig} height={280} />
          ) : (
            <Empty description="No duration data" style={{ marginTop: 80 }} />
          )}
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Events per Hour" style={{ height: 350 }}>
          {eventsData.length > 0 ? (
            <Column {...eventsBarConfig} height={280} />
          ) : (
            <Empty description="No event data" style={{ marginTop: 80 }} />
          )}
        </Card>
      </Col>
    </Row>
  );
}
